import LayoutPage from "components/LayoutPage/LayoutPage";
import {CheckIcon} from "@heroicons/react/solid";
import React, {FC} from "react";
import ButtonPrimary from "components/Button/ButtonPrimary";
import ButtonSecondary from "components/Button/ButtonSecondary";
import {addDoc, collection, doc, onSnapshot} from "firebase/firestore";
import {auth, db} from "../../configs/firebase-config";
import {loadStripe} from "@stripe/stripe-js"
import BgGlassmorphism from "../../components/BgGlassmorphism/BgGlassmorphism";
import {displayToast, toastType} from "../../global/Functions";

export interface PageSubscriptionProps {
  className?: string;
}

export interface PricingItem {
  isPopular: boolean;
  name: string;
  pricing: string;
  desc: string;
  per: string;
  features: string[];
  priceCode: string
}

const pricings: PricingItem[] = [
  {
    isPopular: false,
    name: "Starter",
    pricing: "Free",
    per: "",
    features: ["Restricted Access to CryptoVerse", "Newsletter Sign Up"],
    desc: `An insight into the community`,
    priceCode: "price_1KihtbLGtlSqmT1RWR7VZ7FF",
  },
  {
    isPopular: true,
    name: "Investor",
    pricing: "£5",
    per: "/mo",
    features: [
      "Everything in Starter",
      "Verified Investor Badge",
      "Specialist Financial insight"
    ],
    desc: `Engineered to stay up to date with the latest`,
    priceCode: "price_1KihtzLGtlSqmT1RMtFWARhE",
  },
  {
    isPopular: false,
    name: "Tycoon",
    pricing: "£10",
    per: "/mo",
    features: [
      "Everything in Investor",
      "Verified Tycoon Badge",
      "Exclusive Invite only events",
      "Preview new articles 24hr early"
    ],
    desc: `Cultivated exclusively for the top 1% `,
    priceCode: "price_1KihulLGtlSqmT1RQmYRwRT1",
  },

];

const PageSubscription: FC<PageSubscriptionProps> = ({className = ""}) => {

  const sendToCheckout = (price: any) => {
    if (auth.currentUser?.uid) {
      const docRef = doc(db, "users", `${auth.currentUser.uid}`);
      const checkOutRef = collection(docRef, "checkout_sessions");
      addDoc(checkOutRef, {
        price,
        success_url: "https://thecryptoverse.tech/dashboard",
        cancel_url: "https://thecryptoverse.tech",
      }).then((result) => {
        onSnapshot(doc(checkOutRef, result.id), async (snapshot) => {
          const sessionId = snapshot.get("sessionId");
          const error = snapshot.get("error");
          if (sessionId) {
            const stripePromise = await loadStripe("pk_test_51KiMCmLGtlSqmT1R86ROIchxHg4EyNftxT4kN2MGqd4Jwge5EQg4NOo89kc66tiNWbpzzOuII9R62o1SkrSNE2vB00pGSs8ZfL");
            await stripePromise?.redirectToCheckout({sessionId});
          }
          if (error) displayToast(error.code, toastType.error);
        })
      }).catch((error) => {
        displayToast(error.code, toastType.error);
      })
    }
  }
  const renderPricingItem = (pricing: PricingItem, index: number) => {
    return (
      <div
        key={index}
        className={`h-full relative px-6 py-8 rounded-3xl border-2 flex flex-col overflow-hidden ${
          pricing.isPopular
            ? "border-primary-500"
            : "border-neutral-100 dark:border-neutral-700"
        }`}
      >
        {pricing.isPopular && (
          <span
            className="bg-primary-500 text-white px-3 py-1 tracking-widest text-xs absolute right-3 top-3 rounded-full z-10">
            POPULAR
          </span>
        )}
        <div className="mb-8">
          <h3 className="block text-sm uppercase tracking-widest text-neutral-6000 dark:text-neutral-300 mb-2 font-medium">
            {pricing.name}
          </h3>
          <h2 className="text-5xl leading-none flex items-center">
            <span>{pricing.pricing}</span>
            <span className="text-lg ml-1 font-normal text-neutral-500">
              {pricing.per}
            </span>
          </h2>
        </div>
        <nav className="space-y-4 mb-8">
          {pricing.features.map((item, index) => (
            <li className="flex items-center" key={index}>
              <span className="mr-4 inline-flex flex-shrink-0 text-primary-6000">
                <CheckIcon className="w-5 h-5" aria-hidden="true"/>
              </span>
              <span className="text-neutral-700 dark:text-neutral-300">
                {item}
              </span>
            </li>
          ))}
        </nav>
        <div className="flex flex-col mt-auto">
          {pricing.isPopular ? (
            <ButtonPrimary onClick={() => sendToCheckout(pricing.priceCode)}>Submit</ButtonPrimary>
          ) : (
            <ButtonSecondary onClick={() => sendToCheckout(pricing.priceCode)}>
              <span className="font-medium">Submit</span>
            </ButtonSecondary>
          )}
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-3">
            {pricing.desc}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`PageSubscription ${className}`}
      data-nc-id="PageSubscription"
    >
      <BgGlassmorphism/>
      <LayoutPage
        subHeading="Gain exclusive access to the latest Web3 community"
        headingEmoji=""
        heading="Subscription"
      >
        <section className="text-neutral-600 text-sm md:text-base overflow-hidden">
          <div className="grid lg:grid-cols-3 gap-5 xl:gap-8">
            {pricings.map(renderPricingItem)}
          </div>
        </section>
      </LayoutPage>
    </div>
  );
};

export default PageSubscription;
